import React from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";

import CustomMenu from "components/atoms/CustomMenu";
import CustomModal from "components/atoms/CustomModal";
import CustomTab from "components/atoms/CustomTab";
import ScheduleAppointment from "./ScheduleAppointment";
import AppointmentTable from "./AppointmentTable";

import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import Scheduler from "components/molecules/customScheduler/Scheduler";
import CustomDatePicker from "components/atoms/DatePicker";
import { daysOfThWeek } from "components/molecules/customScheduler/util";
import CsvDownloader from "react-csv-downloader";

import { groupBy } from "utils/groupByFunc";
import { exportToExcel } from "utils/exportToExcel";
import { GET_SPECIALIST_APPOINTMENTS } from "utils/reactQueryKeys";

const dropDownContent = [
  { name: "Daily", value: "Day" },
  { name: "Weekly", value: "Week" },
  { name: "Monthly", value: "Month" },
];

const viewTabs = [
  { label: "Calendar View", id: 0 },
  { label: "Table View (History)", id: 1 },
];

const formatExportData = (data) => {
  if (data?.length) {
    return data.map((d) => {
      const formartedData = {
        ...d,
        doctorName: `${d?.doctor?.fullName || ""}`,
        doctorId: `${d?.doctor?._id || ""}`,
        patientName: `${d?.patient?.firstName || ""} ${d?.patient?.lastName || ""}`,
        patientId: `${d?.patient?._id || ""}`,
      };
      delete formartedData.doctor;
      delete formartedData.patient;
      return formartedData;
    });
  }
  return [];
};

const columns = [
  { id: "_id", displayName: "id" },
  { id: "appointmentStatus", displayName: "appointmentStatus" },
  { id: "createdAt", displayName: "createdAt" },
  { id: "doctorName", displayName: "doctorName" },
  { id: "doctorId", displayName: "doctorId" },
  { id: "patientName", displayName: "patientName" },
  { id: "patientId", displayName: "patientId" },
  { id: "startDateTime", displayName: "startDate" },
  { id: "endDateTime", displayName: "endDate" },
  { id: "startTime", displayName: "startTime" },
  { id: "endTime", displayName: "endTime" },
  { id: "frequency", displayName: "frequency" },
  { id: "isSeenCompleted", displayName: "isSeenCompleted" },
];

const getOnlyDate = (date) => {
  const yr = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${yr}-${month}-${day}`;
};

function ScheduleComponent() {
  const modalRef = React.useRef(null);
  const [scheduleDate, setscheduleDate] = React.useState(new Date());
  const [dateRange, setdateRange] = React.useState(daysOfThWeek(new Date()));
  const [views, setviews] = React.useState({ name: "Weekly", value: "Week" });
  
  // Tab and Pagination States
  const [activeTab, setActiveTab] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // 1. Existing Query: Calendar View (Specialist Appointments)
  const { data: specialistAppointments } = useCustomQuery(
    [
      GET_SPECIALIST_APPOINTMENTS,
      getOnlyDate(dateRange[0]),
      getOnlyDate(dateRange[dateRange.length - 1]),
    ],
    {
      url: `/appointments/get-specialist-appointments`,
      method: "post",
      avoidCancelling: true,
      data: {
        startDate: getOnlyDate(dateRange[0]),
        endDate: getOnlyDate(dateRange[dateRange.length - 1]),
      },
    },
    { refetchOnWindowFocus: false, enabled: activeTab === 0 }
  );

  const { data: dayViewSpecialistAppointments, isLoading: isCalendarLoading } = useCustomQuery(
    [GET_SPECIALIST_APPOINTMENTS, getOnlyDate(scheduleDate)],
    {
      url: `/appointments/get-specialist-appointments`,
      method: "post",
      avoidCancelling: true,
      data: { startDate: getOnlyDate(scheduleDate) },
    },
    {
      refetchOnWindowFocus: false,
      select: (data) => {
        if (data.data.appointments.length) {
          const extractDocId = data.data.appointments.map((appointment) => {
            return { ...appointment, doctorId: appointment?.doctor?._id };
          });
          const groupByDoctor = groupBy(extractDocId, "doctorId");
          delete groupByDoctor["undefined"];
          const groupByDoctorKeys = Object.keys(groupByDoctor);
          return groupByDoctorKeys.map((key) => {
            return {
              doctorDetails: groupByDoctor[key][0].doctor,
              shifts: groupByDoctor[key],
              day: new Date(groupByDoctor[key][0].startDateTime).getDate(),
            };
          });
        }
      },
      enabled: views.value === "Day" && activeTab === 0,
    }
  );

  // 2. New Query: Table View (All History)
  const { data: allAppointmentsResponse, isLoading: isTableLoading } = useCustomQuery(
    ["GET_ALL_APPOINTMENTS_HISTORY", page, rowsPerPage],
    {
      url: `/appointments/get-all-appointments?page=${page + 1}&limit=${rowsPerPage}`,
      method: "get",
    },
    { 
      refetchOnWindowFocus: false, 
      enabled: activeTab === 1 // Only fetch when table tab is active
    }
  );

  const handleClickDropdownItem = (item) => setviews(item);
  
  const handleSelectDate = (date) => {
    const dateRange = daysOfThWeek(date);
    setscheduleDate(date);
    setdateRange(dateRange);
  };
  
  const handleReset = () => setscheduleDate(new Date());
  
  const handleExport = (item) => {
    if (item.name === "Excel") {
      const exportSource = activeTab === 0 
        ? specialistAppointments?.data?.appointments 
        : allAppointmentsResponse?.data?.appointments;
      const data = formatExportData(exportSource);
      exportToExcel(data, "appointment_data_excel");
    }
  };

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "auto" }}>
        
        {/* Toggle between Calendar and Table */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}>
          <CustomTab navItems={viewTabs} value={activeTab} setValue={setActiveTab} />
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ p: 1, pt: 2 }}
        >
          {/* Hide Calendar dropdown if in Table view */}
          {activeTab === 0 ? (
            <CustomMenu
              caption={views.name}
              items={dropDownContent}
              onClickItem={handleClickDropdownItem}
              popperSx={{ width: { xs: "25vw", sm: "8vw" } }}
            />
          ) : <Box />} 

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {activeTab === 0 && (
              <>
                <Button startIcon={<RefreshIcon />} variant="outlined" onClick={handleReset}>
                  Reset
                </Button>
                <CustomDatePicker
                  type="date"
                  views={["year", "month", "day"]}
                  size="small"
                  datePickerSx={{ width: "140px" }}
                  setdate={handleSelectDate}
                  date={scheduleDate}
                  disableFuture={false}
                  name="schedule_date"
                />
              </>
            )}
            <CustomMenu
              caption="Export"
              icon={<FileDownloadOutlinedIcon />}
              onClickItem={handleExport}
              popperSx={{ width: { xs: "26vw", sm: "9vw" } }}
              disabled={
                (activeTab === 0 && specialistAppointments?.data?.appointments?.length === 0) ||
                (activeTab === 1 && allAppointmentsResponse?.data?.appointments?.length === 0)
              }
              items={[
                {
                  name: (
                    <CsvDownloader
                      filename={`appointment_data`}
                      extension=".csv"
                      columns={columns}
                      datas={formatExportData(
                        activeTab === 0 
                          ? specialistAppointments?.data?.appointments 
                          : allAppointmentsResponse?.data?.appointments
                      )}
                      style={{ width: "100%" }}
                    >
                      <Typography>CSV</Typography>
                    </CsvDownloader>
                  ),
                },
                { name: "Excel" },
              ]}
            />
            <Button
              variant="contained"
              color="secondary"
              sx={{ width: "199px" }}
              onClick={() => modalRef?.current?.handleToggle()}
            >
              Schedule appointment
            </Button>
          </Stack>
        </Stack>

        <Paper sx={{ minWidth: 650, width: "100%", overflow: "auto", p: activeTab === 1 ? 2 : 0 }}>
          {activeTab === 0 ? (
            <Scheduler
              view={views.value}
              apiData={specialistAppointments?.data?.appointments || []}
              dayApiData={dayViewSpecialistAppointments || []}
              currentDay={scheduleDate}
              cellType="appointment"
              isLoading={isCalendarLoading}
            />
          ) : (
            <AppointmentTable 
              data={allAppointmentsResponse?.data?.appointments || []} 
              totalRecords={allAppointmentsResponse?.data?.count || 0}
              isLoading={isTableLoading} 
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </Paper>
      </Paper>

      <CustomModal
        ref={modalRef}
        childrenContSx={{ p: 3, height: "100% !important", minHeight: "85vh", minWidth: "75vw" }}
      >
        <ScheduleAppointment />
      </CustomModal>
    </>
  );
}

export default ScheduleComponent;